import { useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import SignaturePad from "../SignaturePad";
import { useLanguage } from "../../i18n/LanguageContext";

export default function ExitDialog({ visit, onClose, onConfirm }) {
  const { t, lang } = useLanguage();
  const [signature, setSignature] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!visit) return null;

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(lang === "en" ? "en-US" : "es-MX", { hour: "2-digit", minute: "2-digit" });

  const handleConfirm = async () => {
    setSaving(true);
    setError("");
    try {
      await onConfirm(visit._id, { signature, notes: notes.trim() });
    } catch (err) {
      setError(err.response?.data?.error || t("visitantes.errGeneric"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!visit} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {t("visitantes.registerExit")}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={600}>{visit.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {visit.isProvider
              ? t("visitantes.provider")
              : visit.contactId
              ? t("visitantes.visitingTo", visit.contactId.name, visit.contactId.unit)
              : t("visitantes.noContact")}
            {" · "}
            {t("visitantes.entryAt", fmtTime(visit.entryAt))}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {t("visitantes.signatureOptional")}
        </Typography>
        <SignaturePad onChange={setSignature} />

        <TextField
          label={t("visitantes.notesOptional")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          sx={{ mt: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" startIcon={<LogoutIcon />} onClick={handleConfirm} disabled={saving}>
          {saving ? t("common.saving") : t("visitantes.confirmExit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
